import emailValidate from "services/emailValidate";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { __ } from "services/Translator";

const NAME_REGEX = /^[a-zA-Z\u00C0-\u024F\s'-]+$/;
const CITY_REGEX = /^[a-zA-Z\u00C0-\u024F\s'.-]+$/;
const STREET_REGEX = /^[a-zA-Z0-9\u00C0-\u024F\s.,'#/-]+$/;

export const sanitizeTextInput = (value, maxLength = 250) =>
  String(value ?? "")
    .replace(/<[^>]*>/g, "")
    .replace(/[<>"'`;(){}[\]\\]/g, "")
    .trim()
    .slice(0, maxLength);

export const validatePersonName = (value, fieldLabel) => {
  const cleaned = sanitizeTextInput(value, 80);
  if (cleaned.length < 2) {
    return { valid: false, value: cleaned, error: __(`Please enter your ${fieldLabel}`) };
  }
  if (!NAME_REGEX.test(cleaned)) {
    return {
      valid: false,
      value: cleaned,
      error: __("Only letters, spaces, hyphens, and apostrophes are allowed"),
    };
  }
  return { valid: true, value: cleaned, error: "" };
};

export const validateEmailField = (value) => {
  const cleaned = sanitizeTextInput(value, 250).toLowerCase();
  if (!cleaned) {
    return { valid: false, value: cleaned, error: __("Please enter your email") };
  }
  if (!emailValidate(cleaned)) {
    return { valid: false, value: cleaned, error: __("Please enter a valid email address") };
  }
  return { valid: true, value: cleaned, error: "" };
};

export const validateCity = (value) => {
  const cleaned = sanitizeTextInput(value, 100);
  if (!cleaned) {
    return { valid: false, value: cleaned, error: __("Please enter your city") };
  }
  if (!CITY_REGEX.test(cleaned)) {
    return {
      valid: false,
      value: cleaned,
      error: __("City cannot contain numbers or special characters"),
    };
  }
  return { valid: true, value: cleaned, error: "" };
};

export const validateStreetAddress = (value) => {
  const cleaned = sanitizeTextInput(value, 200);
  if (!cleaned) {
    return { valid: false, value: cleaned, error: __("Please enter your street address") };
  }
  if (!STREET_REGEX.test(cleaned)) {
    return {
      valid: false,
      value: cleaned,
      error: __("Street address contains invalid characters"),
    };
  }
  return { valid: true, value: cleaned, error: "" };
};

export const validatePostalCode = (value, countryCode) => {
  const cleaned = sanitizeTextInput(value, 20).toUpperCase();
  if (!cleaned) {
    return { valid: false, value: cleaned, error: __("Please enter your postal code") };
  }

  const code = String(countryCode || "").toUpperCase();

  if (code === "US") {
    if (!/^\d{5}(-\d{4})?$/.test(cleaned)) {
      return { valid: false, value: cleaned, error: __("Please enter a valid US zip code") };
    }
    return { valid: true, value: cleaned, error: "" };
  }

  if (code === "CA") {
    if (!/^[A-Z]\d[A-Z][ -]?\d[A-Z]\d$/.test(cleaned)) {
      return { valid: false, value: cleaned, error: __("Please enter a valid Canadian postal code") };
    }
    return { valid: true, value: cleaned, error: "" };
  }

  if (code === "GB") {
    if (!/^[A-Z]{1,2}\d[A-Z\d]? ?\d[A-Z]{2}$/.test(cleaned)) {
      return { valid: false, value: cleaned, error: __("Please enter a valid UK postcode") };
    }
    return { valid: true, value: cleaned, error: "" };
  }

  if (!/^[A-Z0-9 -]{2,12}$/i.test(cleaned)) {
    return { valid: false, value: cleaned, error: __("Please enter a valid postal code") };
  }

  return { valid: true, value: cleaned, error: "" };
};

export const validateDateOfBirth = (dateValue) => {
  if (!dateValue) {
    return { valid: false, error: __("Please enter your date of birth") };
  }

  const birth = dateValue instanceof Date ? dateValue : new Date(dateValue);
  if (Number.isNaN(birth.getTime())) {
    return { valid: false, error: __("Please enter a valid date") };
  }

  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age -= 1;
  }

  if (age < 18) {
    return { valid: false, error: __("You must be at least 18 years old") };
  }
  if (age > 100) {
    return { valid: false, error: __("Please enter a valid date of birth") };
  }

  return { valid: true, error: "" };
};

export const dateToProfileParts = (dateValue) => {
  const birth = dateValue instanceof Date ? dateValue : new Date(dateValue);
  return {
    bday: birth.getDate(),
    bmonth: birth.getMonth() + 1,
    byear: birth.getFullYear(),
  };
};

export const profilePartsToDate = (profile) => {
  const { bday, bmonth, byear } = profile || {};
  if (!bday || !bmonth || !byear) return null;
  const d = new Date(Number(byear), Number(bmonth) - 1, Number(bday));
  return Number.isNaN(d.getTime()) ? null : d;
};

export const parsePhoneForProfile = (e164, fallbackCountry = "US") => {
  const raw = String(e164 || "").trim();
  if (!raw) {
    return { valid: false, phoneCode: "", phone: "", error: __("Please enter a valid phone number") };
  }

  const pn = parsePhoneNumberFromString(raw);
  if (!pn || !pn.isValid()) {
    const fallback = parsePhoneNumberFromString(raw, fallbackCountry);
    if (!fallback || !fallback.isValid()) {
      return { valid: false, phoneCode: "", phone: "", error: __("Please enter a valid phone number") };
    }
    return {
      valid: true,
      phoneCode: `+${fallback.countryCallingCode}`,
      phone: fallback.nationalNumber,
      error: "",
    };
  }

  return {
    valid: true,
    phoneCode: `+${pn.countryCallingCode}`,
    phone: pn.nationalNumber,
    error: "",
  };
};

// Per-field input filters — strip characters that can never be valid for a field
// as the user types (name never has digits, house number allows "85c"/"85-c" but
// no other letters, etc). Shared by the Account Details tab and the sign-up form.
export const INPUT_FILTERS = {
  name: (v) => String(v ?? "").replace(/[^a-zA-ZÀ-ɏ\s'-]/g, ""),
  city: (v) => String(v ?? "").replace(/[^a-zA-ZÀ-ɏ\s'.-]/g, ""),
  street: (v) => String(v ?? "").replace(/[^a-zA-Z0-9À-ɏ\s.,'#/-]/g, ""),
  house: (v) => String(v ?? "").replace(/[^0-9a-zA-Z\s/-]/g, "").slice(0, 10),
  zip: (v) =>
    String(v ?? "")
      .replace(/[^a-zA-Z0-9\s-]/g, "")
      .toUpperCase()
      .slice(0, 12),
};

export const isProfileComplete = (profile) => {
  if (!profile) return false;

  const hasName = Boolean(profile.firstName?.trim() && profile.lastName?.trim());
  const hasDob = Boolean(profile.bday && profile.bmonth && profile.byear);
  const hasPhone = Boolean(String(profile.phone || "").trim());
  const hasAddress = Boolean(
    String(profile.address || "").trim() &&
      String(profile.city || "").trim() &&
      String(profile.zip || "").trim()
  );
  const hasCountry = Boolean(String(profile.playerCountry || "").trim());

  return hasName && hasDob && hasPhone && hasAddress && hasCountry;
};

